import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Plus } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { useCurrentUser } from '../auth';
import { sharedFormsWithMixedTokens } from '../../lib/leadRelay/routeForm';
import { listLeadRoutes } from './api';
import { KC } from '../../lib/kyckConnect/links';
import { LeadRoutesTable } from './LeadRoutesTable';

// Lead Routes (docs/lead-relay.md §10.1) — ADMIN-ONLY. A route decides which
// dealership receives a client's leads, so only admins see or edit them; the
// server refuses non-admins too (this gate only picks the screen).
//
// The PROVIDER is a column (LeadRoutesTable): two routes that look alike and
// behave differently is how someone edits the wrong one.
//
// The token TAIL is a column on purpose: the group-wide form token is copied
// onto every route for a dealer, and a column of tails is how a person sees at a
// glance that one store was left on the old token after a rotation.

export function LeadRoutesPage() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const isAdmin = user.role === 'admin';
  const { data: routes = [], isLoading, error } = useQuery({
    queryKey: ['lead-routes'],
    queryFn: listLeadRoutes,
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Lead routes" />
        <p className="text-sm text-muted-foreground">
          Lead routes are admin-only, because a route decides which dealership receives a client's leads. The{' '}
          <Link to={KC.relayLog} className="underline">
            Lead log
          </Link>{' '}
          is open to everyone.
        </p>
      </div>
    );
  }

  // Per dealer form (host): different dealers legitimately carry different
  // tokens, so only routes on the SAME form are compared.
  const mixedHosts = sharedFormsWithMixedTokens(routes);

  return (
    <div>
      <PageHeader
        title="Lead routes"
        description="Where each account's leads go — a dealership store or a club. A lead routes by its account_id, to the one active route on that account; the destination is set here, never looked up from the lead's postcode."
        actions={
          <Button onClick={() => navigate(`${KC.relayRoutes}/new`)}>
            <Plus className="mr-1 h-4 w-4" />
            New route
          </Button>
        }
      />

      {mixedHosts.length > 0 && (
        <div className="mb-4 flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            Active routes on {mixedHosts.join(', ')} carry different form tokens. One group-wide token is copied onto every
            route, so a route left on an old token fails silently while the others keep working — compare the Token column.
          </p>
        </div>
      )}

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-sm text-red-700">{(error as Error).message}</p>
      ) : routes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No routes yet.</p>
      ) : (
        <LeadRoutesTable routes={routes} onOpen={(id) => navigate(`${KC.relayRoutes}/${id}`)} />
      )}
    </div>
  );
}
